import React, { useCallback, useEffect, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { createConsoleKey, listConsoleKeys, revokeConsoleKey } from 'api/consoleKeys';
import { GREEN, MONO, ago } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const fail = (e, fb) => e?.response?.data?.detail || e?.message || fb;

export default function ConsoleKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [created, setCreated] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try { setKeys((await listConsoleKeys()).results || []); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load your keys.') }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { setCreated(await createConsoleKey(name.trim())); setName(''); await load(); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not create the key.') }); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working at once.`)) return;
    try { await revokeConsoleKey(k.id); setToast({ severity: 'success', message: 'Key revoked.' }); await load(); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not revoke it.') }); }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setToast({ severity: 'success', message: 'Copied.' }); } catch { window.prompt('Copy:', text); }
  };
  const command = created ? `fuzzscan --login --api-key ${created.key} --api-url ${window.location.origin}` : '';
  const active = keys.filter((k) => !k.revoked);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3, maxWidth: 900 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Console keys</Typography>
        <Typography color="text.secondary" variant="body2">Sign in to the fuzzscan console client without your password, so it can save results into your targets here</Typography>
      </Box>
      <Alert severity="info">Without a key the console client still works as a guest: it scans from your own machine and shows the results in the terminal. Nothing is sent here. A key lets it save results into a target of your account. A key cannot change your password or settings, create other keys, or use the admin area.</Alert>

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ ...panel, p: 2.4 }}>
        <TextField size="small" label="Name, for example my laptop" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 80 }} sx={{ flex: '1 1 260px' }} />
        <Button variant="contained" color="success" disabled={!name.trim() || active.length >= 10} onClick={create}>Create a key</Button>
        {active.length >= 10 && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>You have 10 active keys. Revoke one first.</Typography>}
      </Stack>

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        {!keys.length && <Typography color="text.secondary" sx={{ p: 3 }}>No keys yet.</Typography>}
        {keys.map((k) => (
          <Stack key={k.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.6, py: 1.6, borderTop: 1, borderColor: 'divider', opacity: k.revoked ? 0.5 : 1, '&:first-of-type': { borderTop: 0 } }}>
            <Box sx={{ flex: '1 1 220px' }}>
              <Typography sx={{ fontWeight: 600 }}>{k.name}</Typography>
              <Typography variant="caption" sx={{ fontFamily: MONO }} color="text.secondary">{k.prefix}…</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 200px' }}>Created {ago(k.created_at)} · {k.last_used_at ? `last used ${ago(k.last_used_at)}` : 'never used'}</Typography>
            {k.revoked ? <Typography variant="caption" sx={{ fontWeight: 700, color: '#ef4444' }}>REVOKED</Typography> : <Button size="small" color="error" onClick={() => revoke(k)}>Revoke</Button>}
          </Stack>
        ))}
      </Box>

      <Dialog open={Boolean(created)} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>Copy your key now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This is the only time the key is shown. If you lose it, revoke it and make a new one.</Alert>
          <Typography variant="overline" color="text.secondary">Sign in from your terminal</Typography>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(GREEN, 0.07), border: 1, borderColor: alpha(GREEN, 0.3), fontFamily: MONO, fontSize: 12.5, wordBreak: 'break-all', color: GREEN }}>{command}</Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => copy(created.key)}>Copy the key</Button>
          <Button variant="contained" color="success" onClick={() => copy(command)}>Copy the command</Button>
          <Button color="inherit" onClick={() => setCreated(null)}>Done</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
